import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiRequestError } from '../lib/api.js';
import { relativeAge, absoluteTime, shortAddress } from '../lib/format.js';

/**
 * Trade intents.
 *
 * The record of every proposal and what happened to it, including the ones
 * policy refused. A refused intent is the most useful row on this page — it is
 * the answer to "why did nothing happen" — so it is shown with its failed checks
 * rather than hidden as noise.
 *
 * INVARIANT 6 (docs/DESIGN_HANDOFF.md §4.6): a refusal lists **every** failed
 * check, each with its own detail line. Not a count, not the first one. The gate
 * evaluates all of them and returns all of them precisely so the operator can
 * fix everything in one pass — summarising here would throw that away and send
 * them round the loop once per problem.
 *
 * There is no approve button. `TELEGRAM_APPROVAL_REQUIRED` means only a Telegram
 * approval counts, and a button that always fails is worse than no button.
 * Reject is present, because stopping a trade is never gated.
 */

interface PolicyCheck {
  id: string;
  label: string;
  passed: boolean;
  detail: string;
}

interface PolicySnapshot {
  allowed?: boolean;
  checks?: PolicyCheck[];
  failedCheckIds?: string[];
  reason?: string;
  stage?: string;
}

interface TradeDto {
  id: string;
  tx_hash: string;
  status: string;
  wallet_address: string;
  function_name: string;
  spend_amount_eth: string;
  min_amount_out_wei: string;
  /** Actual tokens received, decoded from the receipt logs. Null until confirmed. */
  amount_out_wei: string | null;
  nonce: number;
  gas_used: string | null;
  effective_gas_price: string | null;
  block_number: string | null;
  submitted_at: string;
  confirmed_at: string | null;
  error: string | null;
}

interface IntentDto {
  id: string;
  pair_id: string;
  token_address: string;
  spend_amount_wei: string;
  spend_amount_eth: string;
  max_slippage_bps: number;
  risk_score_snapshot: number;
  approval_channel: string;
  two_step_required: boolean;
  first_step_at: string | null;
  /** Who made the first of two required taps, when different from the confirming tap. */
  first_step_by_telegram_user_id: string | null;
  status: string;
  expires_at: string;
  expired: boolean;
  approved_by_telegram_user_id: string | null;
  approved_at: string | null;
  rejected_at: string | null;
  tx_hash: string | null;
  failure_reason: string | null;
  policy: PolicySnapshot | null;
  trades: TradeDto[];
  created_at: string;
}

/**
 * The accent colour for an intent's left edge.
 *
 * Uses the verdict palette, never armed or safed: the outcome of one trade is
 * not a statement about whether the machine can spend.
 */
const OUTCOME_ACCENT: Record<string, string> = {
  confirmed: 'var(--verdict-qualified)',
  submitted: 'var(--verdict-watch)',
  executing: 'var(--verdict-watch)',
  approved: 'var(--verdict-watch)',
  pending_approval: 'var(--verdict-watch)',
  policy_failed: 'var(--alarm)',
  failed: 'var(--alarm)',
  rejected: 'var(--unlit)',
  expired: 'var(--unlit)',
};

const TRADE_TONE: Record<string, string> = {
  submitted: 'neutral',
  confirmed: 'neutral',
  failed: 'alarm',
  // Not `failed`: we do not know it failed, only that we stopped waiting.
  // Rendering a guess as a fact would misstate the wallet's history.
  dropped: 'alarm',
};

function statusLabel(intent: IntentDto): string {
  if (intent.status === 'pending_approval' && intent.expired) return 'expired';
  return intent.status.replace(/_/g, ' ');
}

/**
 * How far actual execution landed from the authorized floor, in basis
 * points (positive = better than the floor). BigInt throughout — these are
 * wei amounts in the target token's own decimals, and the ratio is
 * decimal-agnostic since both sides share the same units, but a float
 * would still risk losing precision on a large amount. Never touches a
 * float, same rule as every other wei figure in this app.
 */
function executionDeltaBps(minWei: string, actualWei: string): number | null {
  try {
    const min = BigInt(minWei);
    const actual = BigInt(actualWei);
    if (min === 0n) return null;
    return Number(((actual - min) * 10_000n) / min);
  } catch {
    return null;
  }
}

export function TradeIntents() {
  const [intents, setIntents] = useState<IntentDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await api.get<{ intents: IntentDto[] }>('/trade-intents');
      setIntents(result.intents);
      setError(null);
    } catch (err) {
      if (!(err instanceof ApiRequestError && err.isUnauthorized)) {
        setError('Could not load trade intents.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    // Pending intents expire on a short clock, so the list refreshes while the
    // operator is looking at it rather than showing a stale live-looking row.
    const timer = setInterval(() => void load(), 20_000);
    return () => clearInterval(timer);
  }, [load]);

  async function reject(intent: IntentDto) {
    setBusyId(intent.id);
    try {
      await api.post(`/trade-intents/${intent.id}/reject`);
      await load();
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Could not reject that intent.');
    } finally {
      setBusyId(null);
    }
  }

  const pending = intents.filter((i) => i.status === 'pending_approval' && !i.expired);

  return (
    <div className="stack">
      <div className="screen-head">
        <h1 className="screen-title">Trade intents</h1>
        <span className="label label-dim">
          {pending.length} awaiting approval · {intents.length} total
        </span>
      </div>

      {error !== null && (
        <p className="notice" data-tone="alarm" role="alert">
          {error}
        </p>
      )}

      {loading && intents.length === 0 ? (
        <p className="notice">Loading intents…</p>
      ) : intents.length === 0 ? (
        <div className="empty">
          <div className="empty-mark" aria-hidden="true" />
          <p className="empty-title">No intents yet</p>
          <p className="empty-body">
            An intent is created when a pair qualifies and the operator or the
            agent proposes a buy. It cannot execute without a Telegram approval,
            and every refusal is recorded here with the reason.
          </p>
        </div>
      ) : (
        <div className="stack">
          {intents.map((intent) => {
            const failed = (intent.policy?.checks ?? []).filter((check) => !check.passed);
            const isPending = intent.status === 'pending_approval' && !intent.expired;
            const isExpanded = expanded === intent.id;

            return (
              <article
                key={intent.id}
                className="panel card-accent"
                style={{
                  borderLeftColor:
                    OUTCOME_ACCENT[intent.expired ? 'expired' : intent.status] ?? 'var(--unlit)',
                }}
              >
                <div className="panel-head">
                  <span className="row" style={{ flexWrap: 'wrap' }}>
                    <span
                      className="chip"
                      data-tone={
                        intent.status === 'policy_failed' || intent.status === 'failed'
                          ? 'alarm'
                          : 'neutral'
                      }
                    >
                      {statusLabel(intent)}
                    </span>
                    <span className="data-sm">{intent.spend_amount_eth} ETH</span>
                    <span className="label label-dim">
                      risk {intent.risk_score_snapshot} · slippage{' '}
                      {(intent.max_slippage_bps / 100).toFixed(2)}%
                    </span>
                  </span>

                  <span className="row">
                    <span className="label label-dim" title={absoluteTime(intent.created_at)}>
                      {relativeAge(intent.created_at)} ago
                    </span>
                    {isPending && (
                      <button
                        className="btn btn-mini btn-danger"
                        type="button"
                        onClick={() => void reject(intent)}
                        disabled={busyId === intent.id}
                      >
                        {busyId === intent.id ? 'Rejecting…' : 'Reject'}
                      </button>
                    )}
                  </span>
                </div>

                <div className="panel-body stack-2">
                  {isPending && (
                    <p className="notice">
                      Awaiting a Telegram approval. Expires{' '}
                      {relativeAge(intent.expires_at)} from creation
                      {intent.two_step_required
                        ? intent.first_step_at
                          ? ' · two-step, 1 of 2 taps done'
                          : ' · two-step approval required'
                        : ''}
                      .
                    </p>
                  )}

                  {intent.approved_by_telegram_user_id && (
                    <p className="panel-note">
                      {/* v2: a two-step approval's first tap and confirming tap can be
                          different Telegram users -- show both when they differ,
                          collapse to one mention when the same user did both. */}
                      {intent.first_step_by_telegram_user_id !== null &&
                      intent.first_step_by_telegram_user_id !== intent.approved_by_telegram_user_id ? (
                        <>
                          First tap by Telegram user{' '}
                          <span className="data-sm">{intent.first_step_by_telegram_user_id}</span>{' '}
                          · confirmed by Telegram user{' '}
                          <span className="data-sm">{intent.approved_by_telegram_user_id}</span> via{' '}
                          {intent.approval_channel}
                        </>
                      ) : (
                        <>
                          Approved by Telegram user{' '}
                          <span className="data-sm">{intent.approved_by_telegram_user_id}</span> via{' '}
                          {intent.approval_channel}
                        </>
                      )}
                      {intent.approved_at ? ` · ${relativeAge(intent.approved_at)} ago` : ''}
                    </p>
                  )}

                  {/* INVARIANT 6 — every failed check, each with its own detail.
                      Never a count, never just the first one. */}
                  {failed.length > 0 && (
                    <div className="hazard-notice" data-tone="alarm">
                      <div className="hazard-notice-title">
                        Refused — {failed.length} check{failed.length === 1 ? '' : 's'} failed
                      </div>
                      <ul className="rules" style={{ marginTop: 'var(--s-2)' }}>
                        {failed.map((check) => (
                          <li className="rule" key={check.id} data-outcome="fail">
                            <span className="rule-bar" aria-hidden="true" />
                            <span>
                              <span className="rule-label">{check.label}</span>
                              <span className="rule-detail">{check.detail}</span>
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {intent.failure_reason !== null && failed.length === 0 && (
                    <p className="notice" data-tone="alarm">
                      {intent.failure_reason}
                    </p>
                  )}

                  {/* The transactions this intent produced. A retry is a new row,
                      so an intent can legitimately carry several (ADR-044). */}
                  {intent.trades.length > 0 && (
                    <div className="stack-sm">
                      <span className="label">
                        {intent.trades.length === 1 ? 'Transaction' : 'Transactions'}
                      </span>
                      {intent.trades.map((trade) => {
                        const bps =
                          trade.amount_out_wei !== null
                            ? executionDeltaBps(trade.min_amount_out_wei, trade.amount_out_wei)
                            : null;

                        return (
                          <div className="card row" key={trade.id} style={{ flexWrap: 'wrap' }}>
                            <span className="chip" data-tone={TRADE_TONE[trade.status] ?? 'muted'}>
                              {trade.status}
                            </span>
                            <code className="data-xs" title={trade.tx_hash}>
                              {shortAddress(trade.tx_hash, 10, 8)}
                            </code>
                            <span className="row-fact">nonce {trade.nonce}</span>
                            {trade.gas_used && <span className="row-fact">gas {trade.gas_used}</span>}
                            {trade.block_number && (
                              <span className="row-fact">block {trade.block_number}</span>
                            )}
                            <span className="spacer" />
                            <span className="row-fact" title={absoluteTime(trade.submitted_at)}>
                              {relativeAge(trade.submitted_at)} ago
                            </span>
                            {trade.error && (
                              <span className="error-text" style={{ width: '100%' }}>
                                {trade.error}
                              </span>
                            )}

                            {/* v2: verify execution quality directly rather than
                                trusting the transaction blindly. */}
                            {trade.amount_out_wei !== null && (
                              <div className="execution-delta" style={{ width: '100%' }}>
                                <div className="execution-row">
                                  <span className="execution-row-label">Requested min amount out</span>
                                  <span className="execution-row-value data">
                                    {trade.min_amount_out_wei} wei
                                  </span>
                                </div>
                                <div className="execution-row">
                                  <span className="execution-row-label">Actual amount received</span>
                                  <span className="execution-row-value data">
                                    {trade.amount_out_wei} wei
                                    {bps !== null && (
                                      <span
                                        className="execution-delta-note"
                                        data-tone={bps >= 0 ? 'good' : 'bad'}
                                      >
                                        {' '}
                                        ({bps >= 0 ? '+' : ''}
                                        {(bps / 100).toFixed(2)}% {bps >= 0 ? 'better' : 'worse'} than floor)
                                      </span>
                                    )}
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <div className="row" style={{ flexWrap: 'wrap' }}>
                    <Link className="back-link" to={`/pairs/${intent.pair_id}`}>
                      View pair →
                    </Link>
                    <span className="spacer" />
                    {intent.policy?.checks && intent.policy.checks.length > 0 && (
                      <button
                        className="btn btn-mini"
                        type="button"
                        onClick={() => setExpanded(isExpanded ? null : intent.id)}
                        aria-expanded={isExpanded}
                      >
                        {isExpanded
                          ? 'Hide all checks'
                          : `All ${intent.policy.checks.length} policy checks`}
                      </button>
                    )}
                  </div>

                  {isExpanded && intent.policy?.checks && (
                    <ul className="rules">
                      {intent.policy.checks.map((check) => (
                        <li
                          className="rule"
                          key={check.id}
                          data-outcome={check.passed ? 'pass' : 'fail'}
                        >
                          <span className="rule-bar" aria-hidden="true" />
                          <span>
                            <span className="rule-head">
                              <span className="rule-label">{check.label}</span>
                              <span
                                className="chip"
                                data-tone={check.passed ? 'muted' : 'alarm'}
                              >
                                {check.passed ? 'pass' : 'fail'}
                              </span>
                            </span>
                            <span className="rule-detail">{check.detail}</span>
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
